import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LuCheck, LuTriangleAlert, LuX } from 'react-icons/lu';

const AdminToastContext = createContext(null);

export function useAdminToast() {
  const notify = useContext(AdminToastContext);
  if (!notify) throw new Error('useAdminToast must be used inside AdminToastProvider.');
  return notify;
}

function AdminToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());
  const nextId = useRef(0);

  const dismiss = useCallback(id => {
    window.clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const notify = useCallback((message, type = 'success') => {
    if (typeof message !== 'string' || !message.trim()) return;
    const id = ++nextId.current;
    const toast = { id, message: message.trim(), type };
    setToasts(current => [...current.slice(-3), toast]);
    timers.current.set(id, window.setTimeout(() => dismiss(id), type === 'error' ? 7000 : 4500));
  }, [dismiss]);

  useEffect(() => () => {
    timers.current.forEach(timer => window.clearTimeout(timer));
    timers.current.clear();
  }, []);

  return (
    <AdminToastContext.Provider value={notify}>
      {children}
      <div className="admin-toast-viewport" aria-live="polite" aria-relevant="additions text">
        {toasts.map(toast => (
          <div className={`admin-toast is-${toast.type}`} key={toast.id} role={toast.type === 'error' ? 'alert' : 'status'}>
            <span className="admin-toast-icon" aria-hidden="true">
              {toast.type === 'error' ? <LuTriangleAlert size={18} /> : <LuCheck size={18} />}
            </span>
            <p>{toast.message}</p>
            <button type="button" onClick={() => dismiss(toast.id)} aria-label="Dismiss notification">
              <LuX size={17} />
            </button>
          </div>
        ))}
      </div>
    </AdminToastContext.Provider>
  );
}

export default AdminToastProvider;
